import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HardHat, Home } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/States";

const KINDS = [
  ["company", "I run a contracting or renovation business", HardHat],
  ["owner_builder", "I'm a homeowner managing my own build", Home],
];

export default function Signup() {
  const { signIn, brand } = useAuth();
  const [params] = useSearchParams();
  const beta = !!brand?.private_beta;
  const [form, setForm] = useState({ company: "", name: "", email: "", password: "", kind: "company", invite_code: params.get("code") || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const owner = form.kind === "owner_builder";
  const FIELDS = [
    ["company", owner ? "Name your project (e.g. The Lake House)" : "Company name", "text", "organization"],
    ["name", "Your name", "text", "name"],
    ["email", owner ? "Email" : "Work email", "email", "email"],
    ["password", "Password (8+ characters)", "password", "new-password"],
  ];

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/register", { ...form, invite_code: form.invite_code.trim() || null });
      signIn(data, owner ? "/projects?welcome=1" : "/workspace?welcome=1");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout eyebrow={beta ? "Private beta · by invitation" : owner ? "Owner-Builder · free while in preview" : "14-day free trial · no card needed"} title={owner ? "Run your own build" : "Create your workspace"}>
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        {beta && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="signup-beta-notice">
            <p className="text-sm font-medium">E.L.M is in private beta.</p>
            <p className="mt-1 text-xs text-muted-foreground">Company accounts are created by invitation while we test with a small group. Enter the code from your invitation to continue.</p>
            <div className="mt-3 space-y-2"><Label htmlFor="su-code">Invitation code</Label><Input id="su-code" required value={form.invite_code} onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })} placeholder="ELM-XXXXXX" autoComplete="off" className="font-mono uppercase" data-testid="signup-invite-code-input" /></div>
          </div>
        )}
        <div className="grid gap-2" role="radiogroup" aria-label="Who are you?" data-testid="signup-kind">
          {KINDS.map(([k, label, Icon]) => (
            <button type="button" key={k} role="radio" aria-checked={form.kind === k} onClick={() => setForm({ ...form, kind: k })} data-testid={`signup-kind-${k}`}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors ${form.kind === k ? "border-primary bg-primary/5 font-medium" : "hover:bg-secondary/60"}`}>
              <Icon className="h-4 w-4 shrink-0" />{label}
            </button>
          ))}
        </div>
        {FIELDS.map(([k, label, type, ac]) => (
          <div key={k} className="space-y-2">
            <Label htmlFor={`su-${k}`}>{label}</Label>
            <Input id={`su-${k}`} type={type} required minLength={k === "password" ? 8 : undefined} autoComplete={ac} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`signup-${k}-input`} />
          </div>
        ))}
        {error && <p role="alert" className="text-sm text-destructive" data-testid="signup-error">{error}</p>}
        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="signup-submit-button">{busy && <Spinner />} {owner ? "Create my project" : "Start free trial"}</Button>
        <p className="text-xs text-muted-foreground">By continuing you agree to the <Link to="/terms" className="underline underline-offset-4" data-testid="signup-terms-link">Terms</Link> and <Link to="/privacy" className="underline underline-offset-4" data-testid="signup-privacy-link">Privacy policy</Link>.</p>
        <p className="text-xs text-muted-foreground">Already have an account? <Link to="/login" className="underline underline-offset-4" data-testid="signup-login-link">Sign in</Link></p>
      </form>
    </AuthLayout>
  );
}
